/**
 * Checks and sends the site's forms (parts/forms/). This part is small, so it's in app.js; the spam
 * check (turnstile.js) only loads once someone uses a form. The server (includes/Forms.php) checks
 * everything again and has the final say; the messages come from the same definitions
 */
import { listenForDatePickers } from "./date-picker"
import { resetTurnstile, startTurnstile, turnstileToken } from "./turnstile"

const EMAIL_US = "email us at info@capesfoundation.org.au"

const PROBLEMS = {
  interactive: "Please tick the “Verify you are human” box, then send again.",
  failed: `Our spam check didn’t load, so this can’t be sent yet. Please check your connection and try again, or ${EMAIL_US}.`,
  loading: "Our spam check is taking a while. Please wait a moment and send again.",
  network: "Sorry, we couldn’t send this. Check your connection and try again.",
  other: `Sorry, something went wrong. Please try again, or ${EMAIL_US}.`,
}

// The same pattern as WordPress's is_email, which the server uses
const EMAIL = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/

const prepared = new WeakSet()

/**
 * Listen for anyone focusing or sending one of the site's forms. Listening on the document means it
 * works for forms Vue rebuilds (mounting, the news grid's newsletter card)
 */
export const listenForForms = () => {
  document.addEventListener("focusin", (event) => {
    const form = formOf(event.target)
    if (form) {
      prepare(form)
    }
  })

  document.addEventListener("submit", (event) => {
    const form = formOf(event.target)
    if (form) {
      event.preventDefault()
      submit(form)
    }
  })

  // Coming Back to a page the browser kept in memory, a form may still say "Sending…"
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      document.querySelectorAll("form[data-capes-form][data-sending]").forEach(stopSending)
    }
  })

  listenForDatePickers()
}

const formOf = (target) => (target instanceof Element ? target.closest("form[data-capes-form]") : null)

/**
 * Get a form ready as soon as someone starts on it, so the spam check is done by the time they send it
 */
const prepare = (form) => {
  if (prepared.has(form)) {
    return
  }
  prepared.add(form)

  startTurnstile(form)

  // Once someone has tried to send, check each answer again as they change it
  const recheck = (event) => {
    if (!form.dataset.tried || !event.target.matches?.(".cf-input, .nl-input")) {
      return
    }

    showFieldError(form, event.target, problemWith(event.target))

    // Keep the "Please fix N things" box in step, without re-announcing it on every keystroke
    const count = form.querySelectorAll(".cf-field.is-invalid").length
    if (form.dataset.problems !== undefined && +form.dataset.problems !== count) {
      showSummary(form, count)
    }
  }
  form.addEventListener("input", recheck)
  form.addEventListener("change", recheck)
}

const submit = async (form) => {
  prepare(form)

  if (form.dataset.sending) {
    return
  }

  form.dataset.tried = "1"

  const inputs = [...form.querySelectorAll(".cf-input, .nl-input")]
  const problems = inputs.map((input) => [input, problemWith(input)])
  problems.forEach(([input, message]) => showFieldError(form, input, message))

  const wrong = problems.filter(([, message]) => message)
  if (wrong.length) {
    showSummary(form, wrong.length)
    wrong[0][0].focus()
    return
  }

  showSummary(form, 0)
  await send(form)
}

/**
 * @returns {string} what's wrong with the answer, or "" when it's fine
 */
const problemWith = (input) => {
  const value = input.value.trim()
  const invalid = input.dataset.invalid || ""
  const maxLength = +input.dataset.maxLength

  if (!value) {
    return input.dataset.required || ""
  }

  if (maxLength && value.length > maxLength) {
    return `Please keep this under ${maxLength.toLocaleString("en-AU")} characters.`
  }

  if (input.type === "email" && !EMAIL.test(value)) {
    return invalid
  }

  if (input.type === "tel" && !/^\+?[0-9 ()-]{6,}$/.test(value)) {
    return invalid
  }

  if (input.dataset.min !== undefined && (!/^\d+$/.test(value) || +value < +input.dataset.min || +value > +input.dataset.max)) {
    return invalid
  }

  return ""
}

// A date is sent from a hidden field; its date picker button (date-picker.js) shows any problem with it
const shownFor = (input) => (input?.type === "hidden" ? input.closest(".cf-field")?.querySelector(".cf-input") : input)

const isNewsletter = (form) => form.classList.contains("nl-form")

const showFieldError = (form, input, message) => {
  if (message) {
    input.setAttribute("aria-invalid", "true")
  } else {
    input.removeAttribute("aria-invalid")
  }

  if (isNewsletter(form)) {
    form.classList.toggle("is-invalid", !!message)
    showNewsletterMessage(form, message, true)
    return
  }

  const field = input.closest(".cf-field")
  const hint = field.querySelector(".cf-hint")
  let error = field.querySelector(".cf-error")

  field.classList.toggle("is-invalid", !!message)

  if (message) {
    if (!error) {
      error = document.createElement("p")
      error.className = "cf-error"
      error.id = `${input.id}-error`
      field.append(error)
    }
    error.textContent = message
    input.setAttribute("aria-describedby", error.id)
  } else {
    error?.remove()
    if (hint) {
      input.setAttribute("aria-describedby", hint.id)
    } else {
      input.removeAttribute("aria-describedby")
    }
  }
}

/**
 * The box at the top of an enquiry form. count = how many answers need fixing, 0 hides it
 */
const showSummary = (form, count, problem = "") => {
  const summary = form.querySelector(".cf-summary")

  if (!summary) {
    return
  }

  summary.replaceChildren()
  summary.hidden = !count && !problem

  if (count) {
    form.dataset.problems = count
  } else {
    delete form.dataset.problems
  }

  if (count) {
    const strong = document.createElement("strong")
    strong.textContent = `Please fix ${count === 1 ? "1 thing" : `${count} things`} below`
    summary.append(strong, " and send it again.")
  } else if (problem) {
    summary.textContent = problem
    summary.focus()
  }
}

const showNewsletterMessage = (form, message, isError) => {
  const box = form.querySelector(".nl-msg")
  box.textContent = message
  box.classList.toggle("is-error", !!message && isError)
}

const showProblem = (form, message) => {
  if (isNewsletter(form)) {
    showNewsletterMessage(form, message, true)
  } else {
    showSummary(form, 0, message)
  }
}

const startSending = (form) => {
  const button = form.querySelector("[type=submit]")
  form.dataset.sending = "1"
  form.dataset.label = button.textContent
  button.disabled = true
  button.textContent = isNewsletter(form) ? "Subscribing…" : "Sending…"
}

const stopSending = (form) => {
  const button = form.querySelector("[type=submit]")
  delete form.dataset.sending
  delete form.dataset.leaving
  button.disabled = false
  button.textContent = form.dataset.label || button.textContent
}

const send = async (form) => {
  startSending(form)

  let posted = false

  try {
    // Only send with the spam check passed: without it the server would turn the send away
    const check = await turnstileToken(form)
    if (!check.token && check.status !== "off") {
      showProblem(form, PROBLEMS[check.status] || PROBLEMS.failed)
      return
    }

    const values = Object.fromEntries(new FormData(form))
    values["cf-turnstile-response"] = check.token
    values.page = window.location.href

    posted = true
    const response = await fetch(form.action, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(values),
    })
    const data = await response.json().catch(() => ({}))

    if (response.ok && data.ok) {
      done(form, data)
      return
    }

    if (response.status === 422 && data.errors) {
      const wrong = Object.entries(data.errors)
        .map(([name, message]) => [shownFor(form.elements[name]), message])
        .filter(([input]) => input)
      wrong.forEach(([input, message]) => showFieldError(form, input, message))
      showSummary(form, wrong.length)
      wrong[0]?.[0].focus()
      return
    }

    showProblem(form, data.message || PROBLEMS.other)
  } catch {
    showProblem(form, PROBLEMS.network)
  } finally {
    if (form.isConnected && !form.dataset.leaving) {
      stopSending(form)
      if (posted) {
        resetTurnstile(form)
      }
    }
  }
}

const done = (form, { redirect, message }) => {
  if (redirect) {
    // Keep the button on "Sending…" while the thank-you page loads
    form.dataset.leaving = "1"
    window.location.assign(redirect)
    return
  }

  const thanks = document.createElement("div")
  thanks.className = isNewsletter(form)
    ? `nl-done${form.classList.contains("nl-form--card") ? " nl-done--card" : ""}`
    : "cf-done"
  thanks.setAttribute("role", "status")
  thanks.tabIndex = -1
  form.replaceWith(thanks)

  // Text added after the element is in place is announced more reliably; moving focus there and
  // into view keeps people from wondering where the form went
  requestAnimationFrame(() => {
    thanks.textContent = message
    thanks.focus({ preventScroll: true })
    thanks.scrollIntoView({ block: "center", behavior: "smooth" })
  })
}
